import {
  AccountEnvironment,
  Page,
  UserRepresentation,
  getPersonalInfo,
  savePersonalInfo,
  useEnvironment,
} from "@keycloak/keycloak-account-ui";
import { 
  ActionGroup,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  TextInput,
  AlertVariant,
} from "@patternfly/react-core";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { validateValue, validateBirthday, validateAddressGroup } from "./validators";
import { BirthdaySelect } from "./BirthdaySelect";
import { SexRadioGroup } from "./SexRadioGroup";
import { ZipCodeInput } from "./ZipCodeInput";
import { AccountIdField } from "./AccountIdField";
import { useContentAlert } from "./ContentAlert";

type NCFormGroupProps = {
  attr: any;
  personalInfo: any;
  setPersonalInfo: React.Dispatch<React.SetStateAction<any>>;
  errors: Record<string, string | null>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  autoFillMode: boolean;
  setAutoFillMode: React.Dispatch<React.SetStateAction<boolean>>;
};

interface UserProfileAttribute {
  name: string;
  displayName?: string;
  validators?: Record<string, any>;
}

export const NCFormGroup = ({
  attr,
  personalInfo,
  setPersonalInfo,
  errors,
  setErrors,
  autoFillMode,
  setAutoFillMode
}: NCFormGroupProps) => {
  const { t } = useTranslation();

  const isStandardField = ["firstName", "lastName"].includes(attr.name);

  const rawValue = isStandardField
    ? personalInfo?.[attr.name] ?? ""
    : personalInfo?.attributes?.[attr.name]?.[0] ?? "";

  const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;

  const onChange = (name: string, v: string) => {
    setPersonalInfo((prev: any) => {
      let updated;

      // firstName / lastName は attributes 直下
      if (["firstName", "lastName"].includes(name)) {
        updated = {
          ...prev,
          [name]: v,
        };
      } else {
        updated = {
          ...prev,
          attributes: {
            ...prev.attributes,
            [name]: [v],
          },
        };
      }

      // 単体バリデーション
      const singleError = validateValue(attr, v);
      setErrors(prev => ({
        ...prev,
        [name]: singleError,
      }));

      return updated;
    });
  };

  return (
    <FormGroup
      label={
        <>
          {["zip_code", "phone_number"].includes(attr.name)

            ? `${t(attr.name)}${t("halfWidth-digit")}`
            : t(attr.name)}

          {attr.annotations?.formRequired === "true" && (
            <span className="nc-account-form_label_required">{t("required")}</span>
          )}
        </>
      }
    >
      {/* アカウントID */}
      {attr.annotations?.formtype === "account_id" && <AccountIdField value={value} />}

      {/* 誕生日 */}
      {attr.annotations?.formtype === "birthday" && (
        <BirthdaySelect
          attr={attr}
          personalInfo={personalInfo}
          setPersonalInfo={setPersonalInfo}
          setErrors={setErrors}
        />
      )}

      {/* 性別 */}
      {attr.annotations?.formtype === "sex" && (
        <SexRadioGroup value={value} onChange={v => onChange(attr.name, v)} />
      )}

      {/* 郵便番号 */}
      {attr.annotations?.formtype === "zipCode" && (
        <ZipCodeInput
          attr={attr}
          placeholder={t(attr.annotations.inputTypePlaceholder)}
          personalInfo={personalInfo}
          setPersonalInfo={setPersonalInfo}
          errors={errors}
          setErrors={setErrors}
          autoFillMode={autoFillMode}
          setAutoFillMode={setAutoFillMode}
        />
      )}

      {/* 都道府県, 市区町村, 町名番地, 建物 */}
      {["prefectures", "municipalities", "street", "building"].includes(attr.annotations?.formtype) && (
        <TextInput
          id={attr.name}
          name={attr.name}
          value={personalInfo.attributes?.[attr.name]?.[0] ?? ""}
          placeholder={t(attr.annotations.inputTypePlaceholder)}
          validated={errors[attr.name] ? "error" : "default"}
          maxLength={attr.validators?.length?.max ?? undefined}
          onChange={(_, v) => {
            setPersonalInfo((prev:any) => {
              const updated = {
                ...prev,
                attributes: {
                  ...prev.attributes,
                  [attr.name]: [v],
                },
              };

              const correlationErrors = validateAddressGroup(updated.attributes, false);
              setErrors(prevErrors => ({ ...prevErrors, ...correlationErrors }));

              return updated;
            });
          }}
        />
      )}

      {/* その他 */}
      {attr.annotations?.formtype == null && (
        <TextInput
          id={attr.name}
          name={attr.name}
          value={value}
          onChange={(_, v) => onChange(attr.name, v)}
          placeholder={t(attr.annotations?.inputTypePlaceholder ?? "")}
          validated={errors[attr.name] ? "error" : "default"}
          maxLength={attr.validators?.length?.max ?? undefined}
        />
      )}

      {attr.name !== "account_id" && errors[attr.name] && (
        <FormHelperText className="error-text">
          {(() => {
            const key = errors[attr.name] as string;
            const translated = t(key);
            const cleaned = translated.replace(
              new RegExp(`^${t(attr.name)}:\\s*`),
              ""
            );
            return cleaned;
          })()}
        </FormHelperText>
      )}
      
    </FormGroup>
  );
};

export const PersonalInfo = () => {
  const { t } = useTranslation();
  const context = useEnvironment<AccountEnvironment>();
  const [personalInfo, setPersonalInfo] = useState<UserRepresentation>();

  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [autoFillMode, setAutoFillMode] = useState(false);

  const hasError = Object.values(errors).some(e => e !== null);

  useEffect(() => {
    const controller = new AbortController();

    getPersonalInfo({ signal: controller.signal, context })
      .then(setPersonalInfo)
      .catch(err => {
        if (err.name === "AbortError") {
          return;
        }
        console.error(err);
      });

    return () => controller.abort();
  }, []);

  const { postAlert } = useContentAlert();

  if (!personalInfo) return <div>Loading...</div>;

  const attributes = personalInfo.userProfileMetadata.attributes;

  const visibleAttributes = attributes.filter(
    (attr: UserProfileAttribute) =>
      !["locale", "username", "email", "registered_site"].includes(attr.name)
  );

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // -----------------------------
    // 単体チェック
    // -----------------------------
    const singleErrors: Record<string, string | null> = {};

    visibleAttributes.forEach((attr: any) => {
      const name = attr.name;
      const rawValue =
        ["firstName", "lastName"].includes(name)
          ? personalInfo[name]
          : personalInfo.attributes?.[name]?.[0];

      singleErrors[name] = validateValue(attr, rawValue ?? "");
    });

    // -----------------------------
    // 住所チェック
    // -----------------------------
    const correlationErrors = validateAddressGroup(
      personalInfo.attributes,
      autoFillMode
    );

    // -----------------------------
    // エラーを統合
    // -----------------------------
    const merged: Record<string, string | null> = { ...correlationErrors };

    Object.entries(singleErrors).forEach(([field, err]) => {
      if (err !== null && err !== undefined) {
        merged[field] = err;
      }
    });

    // -----------------------------
    // 生年月日チェック
    // -----------------------------
    const rawBirthday = personalInfo.attributes?.birthday?.[0];
    const birthdayResult = validateBirthday(rawBirthday);

    if (birthdayResult) {
      const msg =
        birthdayResult === "required"
          ? t("screen-valid.error-user-attribute-required-birthday")
          : t("screen-valid.error-pattern-not_exist-date");

      setErrors(prev => ({ ...prev, birthday: msg }));
      postAlert(AlertVariant.danger, "入力内容をご確認ください。");
      return;
    }

    // -----------------------------
    // エラーがあればアラート
    // -----------------------------
    const firstError = Object.values(merged).find(e => e !== null);
    if (firstError) {
      setErrors(merged);
      postAlert(AlertVariant.danger, "入力内容をご確認ください。");
      return;
    }

    // -----------------------------
    // エラーがなければ保存
    // -----------------------------
    try {
      await savePersonalInfo(context, personalInfo);
      postAlert(AlertVariant.success, "");
    } catch (error) {
      console.error(error);
      postAlert(AlertVariant.danger, "入力内容をご確認ください。");
    }
  };

  return (
    <div className="personalInfoPage">
      <Page title={t("personalInfoHtmlTitle")} description={""}>
        <Form isHorizontal onSubmit={onSubmit}>
          {visibleAttributes.map((attr: any) => (
            <NCFormGroup
              key={attr.name}
              attr={attr}
              personalInfo={personalInfo}
              setPersonalInfo={setPersonalInfo}
              errors={errors}
              setErrors={setErrors}
              autoFillMode={autoFillMode}
              setAutoFillMode={setAutoFillMode}
            />
          ))}

          <ActionGroup>
            <Button
              data-testid="save"
              type="submit"
              id="save-btn"
              variant="primary"
              className="nc-button-dosave"
              isDisabled={hasError}
            >
              {t("account.button_doSave")}
            </Button>
          </ActionGroup>
        </Form>
      </Page>
    </div>
  );
};
import React from "react";